/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState, useContext} from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'

import {
    Box,
    Flex,
    IconButton,
    Badge,
    Button,
    Text,
    useMultiStyleConfig,
    useBreakpointValue,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {useAuthHelper, AuthHelpers} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useCurrentBasket} from '../../hooks/use-current-basket'
import {useCustomObject} from '../../hooks/use-custom-object'
import useCustomShopperContext from '../../hooks/use-custom-shopper-context'
import {useSiteCustomPreferences} from '../../hooks/use-site-custom-preferences'
import {useWishList} from '../../hooks/use-wish-list'
import {useMergeWishList} from '../../hooks/use-merge-wishlist'
import {useCurrentCustomer} from '../../hooks/use-current-customer'
import {SegmentContext} from '../../integrations/segment/app/contexts'

import Search from '../search'
import UserIcon from './UserIcon'
import WishlistIcon from './WishlistIcon'
import HamburgerIcon from './HamburgerIcon'
import CloseIcon from './CloseIcon'
import {BrandLogo} from '../../components/icons'
import {HideOnDesktop} from '../responsive'
import GlobalContentSlotHtml from '../content-slot/global-content-slot-html'
import MultiCountriesPopUp from '../multi-countries'
import MiniCart from '../minicart'
import {handleGeoCountryPopup} from '../../utils/custom-utils'
import {mainMenuItemSelected} from '../../integrations/segment/app/page/home'
import {setSessionJSONItem, buildRedirectURI, getSessionJSONItem} from '../../utils/utils'
import {getSSOCookie} from '../../utils/custom-api-utils'

const ENTER_KEY = 'Enter'

/**
 * The header is the main source for accessing
 * navigation, search, basket, and other
 * important information and actions. It persists
 * on the top of your application and will
 * respond to changes in device size.
 *
 * To customize the styles, update the themes
 * in theme/components/project/header.js
 * @param  props
 * @param   {func} props.onMenuClick click event handler for menu button
 * @param   {func} props.onLogoClick click event handler for menu button
 * @param   {object} props.searchInputRef reference of the search input
 * @param   {func} props.onMyAccountClick click event handler for my account button
 * @param   {func} props.onMyCartClick click event handler for my cart button
 * @return  {React.ReactElement} - Header component
 */
const Header = ({
    children,
    onMenuClick = noop,
    onMyAccountClick = noop,
    onLogoClick = noop,
    onMyCartClick = noop,
    onWishlistClick = noop,
    isMenuOpen,
    isCountryModalOpen,
    openCountryModal,
    closeCountryModal,
    hoverDelay,
    ...props
}) => {
    const intl = useIntl()
    const {
        derivedData: {totalItems},
        data: basket
    } = useCurrentBasket()
    const {onOpen: onAccountMenuOpen} = useDisclosure()
    const [isPopupOpen, setIsPopupOpen] = useState(false)
    const {data: customer} = useCurrentCustomer()
    const {site, locale, buildUrl} = useMultiSite()
    const {data: multiCountryList} = useCustomObject({objectTypeId: 'multiCountry'})
    const shopperGeoLocation = useCustomShopperContext()
    const [geoSessionData, setGeoSessionData] = useState(null)
    const segmentContext = useContext(SegmentContext)
    const {getcustomPreferencevalue} = useSiteCustomPreferences()
    const ssoEnable = getcustomPreferencevalue('pwaSSOEnable')

    const rightStufOrderStatusURL = getcustomPreferencevalue('rsOrderStatusUrl')
    useEffect(() => {
        const siteHeader = document.querySelector('.cr-header')
        const handleScroll = () => {
            const promoBannerHeight = document.querySelector('.header-promo-and-svod-block')
            const isMinified = window.scrollY > promoBannerHeight?.offsetHeight

            if (window.innerWidth >= 1024) {
                if (promoBannerHeight) {
                    if (isMinified) {
                        siteHeader.style.transform = `translateY(-${promoBannerHeight.offsetHeight}px)`
                        siteHeader.style.transitionDuration = `.3s`
                    } else {
                        siteHeader.style.transform = 'translateY(0)'
                    }
                } else {
                    // If promo banner is not present → keep header in place
                    siteHeader.style.transform = 'translateY(0)'
                }
            }
        }

        if (window.innerWidth >= 1024) {
            window.addEventListener('scroll', handleScroll)
        }

        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        if (shopperGeoLocation && multiCountryList?.success) {
            handleGeoCountryPopup({
                multiCountryList,
                shopperGeoLocation,
                openCountryModal,
                setGeoSessionData,
                site
            })
        }
    }, [multiCountryList, shopperGeoLocation])

    const styles = useMultiStyleConfig('Header')
    const isMobile = useBreakpointValue({base: true, sm: false, md: false})
    const isTablet = useBreakpointValue({base: false, sm: true, md: false})
    const isDesktop = useBreakpointValue({base: false, lg: true})

    const placeholder = intl.formatMessage({
        id: 'header.field.placeholder.search_for_products',
        defaultMessage: 'What are you looking for?'
    })

    const {data: wishListData} = useWishList()

    const [wishlistCount, setWishListCount] = useState(0)
    const wishlistItems = (wishListData?.productWishList || []).flatMap(
        (list) => list?.customerProductListItems || []
    )
    const {data: mergedWishListData} = useMergeWishList(
        customer?.isRegistered,
        customer?.customerNo
    )

    useEffect(() => {
        const mergedWishListCount = mergedWishListData?.itemsCount || 0
        const currentWishListCount = wishlistItems?.length || 0
        const wishListCountval = currentWishListCount + mergedWishListCount

        setWishListCount(wishListCountval)
    }, [mergedWishListData, wishlistItems])

    const guestUserData = [
        {
            to: '/s/CrunchyrollUS/orderstatus',
            message: <FormattedMessage id="header.order_status" defaultMessage="Order History" />
        },
        {
            to: rightStufOrderStatusURL,
            isExternal: true,
            message: (
                <FormattedMessage
                    id="header.rightstuff_order_status"
                    defaultMessage="Rightstuf Order Status"
                />
            )
        }
    ]

    if (!ssoEnable)
        guestUserData.unshift({
            to: '/login',
            message: <FormattedMessage id="header.login" defaultMessage="Login" />
        })

    const registeredUserData = [
        {
            to: '/account',
            message: <FormattedMessage id="header.my_account" defaultMessage="My Account" />
        },
        {
            to: '/orders',
            message: <FormattedMessage id="header.my_account" defaultMessage="My Orders" />
        },
        {
            to: rightStufOrderStatusURL,
            isExternal: true,
            message: (
                <FormattedMessage
                    id="header.rightstuff_order_status"
                    defaultMessage="Rightstuf Order Status"
                />
            )
        }
    ]
    const authorizeIDP = useAuthHelper(AuthHelpers.AuthorizeIDP)
    const logout = useAuthHelper(AuthHelpers.Logout)
    const configs = getConfig()
    const appOrigin = useAppOrigin()
    const redirectPath = configs?.app?.login?.social?.redirectURI || ''
    const redirectURI = buildRedirectURI(appOrigin, redirectPath, site, locale)

    const onSocialLoginClick = async () => {
        try {
            const location = window?.location?.pathname?.includes('/guest/wishlist')
                ? buildUrl('/account/wishlist')
                : window?.location?.pathname
            setSessionJSONItem('returnToPage', location)
            await authorizeIDP.mutateAsync({
                hint: configs?.app?.login?.social?.ssoIDPName || 'storeCrunchyroll',
                redirectURI: redirectURI
            })
        } catch (error) {
            logger.error('Error in onSocialLoginClick', {
                namespace: 'header.login',
                additionalProperties: {error: error.message}
            })
        }
    }

    const onSocialLoginLogout = async () => {
        try {
            await logout.mutateAsync()
            await getSSOCookie(appOrigin, silentLoginCookie, 'delete')
            window.location.reload()
        } catch (error) {
            logger.error('Error in onSocialLoginLogout', {
                namespace: 'header.logout',
                additionalProperties: {error: error.message}
            })
        }
    }

    const silentLoginCookie = configs?.app?.library?.silentLoginCookie
    const silentLoginEnable = configs?.app?.library?.silentLoginEnable
    const [cookieVal, setCookieVal] = useState('')
    useEffect(() => {
        const getCookie = async () => {
            setSessionJSONItem('etp_rt', null)
            if (!customer?.isRegistered) {
                var result = await getSSOCookie(appOrigin, silentLoginCookie)
                if (result?.value) {
                    setCookieVal(result?.value)
                    setSessionJSONItem('etp_rt', result?.value)
                }
            }
        }
        getCookie()
    }, [])

    useEffect(() => {
        const silentLogin = async () => {
            await onSocialLoginClick()
        }
        const locatedFrom = getSessionJSONItem('returnToPage')

        if (!locatedFrom && cookieVal && !customer?.isRegistered && silentLoginEnable === 'true')
            silentLogin()
    }, [cookieVal])

    return (
        <Box {...props}>
            <Box
                {...{...styles.container, ...styles.promoBgImage}}
                backgroundImage={`url(${getAssetUrl('static/img/header-promo-bg-img.jpg')})`}
                className="header-promo-and-svod-block"
            >
                <Box className="header-banner-desktop" {...styles.headerPromoBlock}>
                    <GlobalContentSlotHtml
                        slotId={'header-promo-banner'}
                        context={'global'}
                        type={'html'}
                    />
                </Box>

                <Box {...styles.svodLinkMainBlock}>
                    <Box className="svod-link-block-desktop">
                        <GlobalContentSlotHtml
                            slotId={'svod-link'}
                            context={'global'}
                            type={'html'}
                        />
                    </Box>
                </Box>
            </Box>

            <Box className="header-logo-block">
                <Box {...styles.container} className="header-block">
                    <Flex
                        alignItems={['center']}
                        justifyContent={'space-between'}
                        wrap={isMobile || isTablet ? 'wrap' : ''}
                    >
                        <HideOnDesktop>
                            <IconButton
                                aria-label={intl.formatMessage({
                                    id: 'header.button.assistive_msg.menu',
                                    defaultMessage: 'Menu'
                                })}
                                title={intl.formatMessage({
                                    id: 'header.button.assistive_msg.menu.open_dialog',
                                    defaultMessage: 'Opens a dialog'
                                })}
                                icon={isMenuOpen ? <CloseIcon /> : <HamburgerIcon />}
                                variant="unstyled"
                                display={{md: 'none'}}
                                className="headerIcons hamburgerIcon"
                                onClick={onMenuClick}
                            />
                        </HideOnDesktop>
                        <Box {...styles.logo}>
                            <Button
                                aria-label={intl.formatMessage({
                                    id: 'header.button.assistive_msg.logo',
                                    defaultMessage: 'Crunchyroll Store Logo'
                                })}
                                onClick={onLogoClick}
                                className="logo-block"
                                variant="unstyled"
                            >
                                <BrandLogo />
                            </Button>
                        </Box>

                        <Box
                            {...styles.searchContainer}
                            width={{
                                base: 'full',
                                sm: 'calc(100% - 415px)',
                                md: 'calc(100% - 490px)'
                            }}
                        >
                            <Search
                                aria-label={placeholder}
                                placeholder={placeholder}
                                {...styles.search}
                            />
                        </Box>

                        <Box {...styles.headerIconsBlock}>
                            <MultiCountriesPopUp
                                isOpen={isCountryModalOpen}
                                openModal={() => {
                                    openCountryModal()
                                    setGeoSessionData(null)
                                }}
                                closeModal={closeCountryModal}
                                preSelectedData={geoSessionData}
                            />

                            {/* My Account */}
                            <IconButton
                                position={'relative'}
                                onFocus={isDesktop ? onAccountMenuOpen : noop}
                                onKeyDown={(e) => {
                                    e.key === ENTER_KEY ? onMyAccountClick() : noop
                                }}
                                onMouseOver={() => {
                                    setIsPopupOpen(true)
                                }}
                                onMouseLeave={() => {
                                    setIsPopupOpen(false)
                                }}
                                className="headerIcons userIcon"
                                aria-label={intl.formatMessage({
                                    id: 'header.button.assistive_msg.my_account',
                                    defaultMessage: 'My account'
                                })}
                                icon={
                                    <>
                                        <UserIcon />
                                        {isPopupOpen &&
                                            (customer?.isRegistered ? (
                                                <Flex {...styles.loginBlock}>
                                                    {registeredUserData?.map((link, index) => {
                                                        return (
                                                            <>
                                                                {link?.isExternal ? (
                                                                    <Button
                                                                        key={index}
                                                                        variant={'link'}
                                                                        color={'white'}
                                                                        height={'auto'}
                                                                        textAlign={'left'}
                                                                        textDecoration={
                                                                            'none !important'
                                                                        }
                                                                        m={0}
                                                                        display={'block'}
                                                                        onClick={() => {
                                                                            setIsPopupOpen(false)
                                                                            mainMenuItemSelected(
                                                                                '',
                                                                                segmentContext
                                                                            )
                                                                            if (link?.to)
                                                                                window.open(
                                                                                    link.to,
                                                                                    '_blank'
                                                                                )
                                                                        }}
                                                                    >
                                                                        <Text
                                                                            {...styles.loginText}
                                                                            className="link-text-image-title"
                                                                        >
                                                                            {link.message}
                                                                        </Text>
                                                                    </Button>
                                                                ) : (
                                                                    <Link
                                                                        key={index}
                                                                        to={link.to}
                                                                        onClick={() => {
                                                                            setIsPopupOpen(false)
                                                                            mainMenuItemSelected(
                                                                                '',
                                                                                segmentContext
                                                                            )
                                                                        }}
                                                                    >
                                                                        <Text
                                                                            {...styles.loginText}
                                                                            className="link-text-image-title"
                                                                        >
                                                                            {link.message}
                                                                        </Text>
                                                                    </Link>
                                                                )}
                                                            </>
                                                        )
                                                    })}
                                                    <Button
                                                        onClick={() => {
                                                            onSocialLoginLogout()
                                                            setIsPopupOpen(false)
                                                        }}
                                                        key={'sso-logout'}
                                                        variant={'link'}
                                                        color={'white'}
                                                        height={'auto'}
                                                        textAlign={'left'}
                                                        textDecoration={'none !important'}
                                                        m={0}
                                                        display={'block'}
                                                    >
                                                        <Text
                                                            {...styles.loginText}
                                                            className="link-text-image-title"
                                                        >
                                                            <FormattedMessage
                                                                id="header.order_status"
                                                                defaultMessage="Log Out"
                                                            />
                                                        </Text>
                                                    </Button>
                                                </Flex>
                                            ) : (
                                                <Flex {...styles.loginBlock}>
                                                    {ssoEnable && (
                                                        <Link
                                                            onClick={() => {
                                                                onSocialLoginClick()
                                                            }}
                                                            to={'#'}
                                                            key={'sso'}
                                                        >
                                                            <Text
                                                                {...styles.loginText}
                                                                className="link-text-image-title"
                                                            >
                                                                <FormattedMessage
                                                                    id="header.login"
                                                                    defaultMessage="Login"
                                                                />
                                                            </Text>
                                                        </Link>
                                                    )}
                                                    {guestUserData?.map((link, index) => {
                                                        return (
                                                            <>
                                                                {link?.isExternal ? (
                                                                    <Button
                                                                        key={index}
                                                                        variant={'link'}
                                                                        color={'white'}
                                                                        height={'auto'}
                                                                        textAlign={'left'}
                                                                        textDecoration={
                                                                            'none !important'
                                                                        }
                                                                        m={0}
                                                                        display={'block'}
                                                                        onClick={() => {
                                                                            setIsPopupOpen(false)
                                                                            segmentContext.linkText =
                                                                                'Log In'
                                                                            mainMenuItemSelected(
                                                                                '',
                                                                                segmentContext
                                                                            )
                                                                            if (link?.to)
                                                                                window.open(
                                                                                    link.to,
                                                                                    '_blank'
                                                                                )
                                                                        }}
                                                                    >
                                                                        <Text
                                                                            {...styles.loginText}
                                                                            className="link-text-image-title"
                                                                        >
                                                                            {link.message}
                                                                        </Text>
                                                                    </Button>
                                                                ) : (
                                                                    <Link
                                                                        key={index}
                                                                        to={link.to}
                                                                        onClick={() => {
                                                                            setIsPopupOpen(false)
                                                                            segmentContext.linkText =
                                                                                'Log In'
                                                                            mainMenuItemSelected(
                                                                                '',
                                                                                segmentContext
                                                                            )
                                                                        }}
                                                                    >
                                                                        <Text
                                                                            {...styles.loginText}
                                                                            className="link-text-image-title"
                                                                        >
                                                                            {link.message}
                                                                        </Text>
                                                                    </Link>
                                                                )}
                                                            </>
                                                        )
                                                    })}
                                                </Flex>
                                            ))}
                                    </>
                                }
                                variant="unstyled"
                            />

                            {/* Wishlist */}
                            <IconButton
                                aria-label={intl.formatMessage({
                                    defaultMessage: 'Wishlist',
                                    id: 'header.button.assistive_msg.wishlist'
                                })}
                                icon={
                                    <>
                                        <WishlistIcon />
                                        {wishlistCount > 0 && (
                                            <Badge
                                                variant="notification"
                                                backgroundColor="headerItemsBadgesBgColor"
                                                {...styles.headerIcons}
                                            >
                                                {wishlistCount}
                                            </Badge>
                                        )}
                                    </>
                                }
                                variant="unstyled"
                                className="headerIcons wishlistIcon"
                                onClick={() => {
                                    onWishlistClick()
                                    segmentContext.linkText = 'Wishlist'
                                    mainMenuItemSelected('', segmentContext)
                                }}
                            />

                            {/* Mini Cart */}
                            <MiniCart
                                basket={basket}
                                totalItems={totalItems}
                                onMyCartClick={onMyCartClick}
                                hoverDelay={hoverDelay}
                            />
                        </Box>
                    </Flex>
                </Box>
            </Box>

            {/* Navigation Menu */}
            <Box
                {...styles.bodyContainer}
                className={isDesktop ? 'desktop-menu-block' : 'mobile-menu-block'}
            >
                {!isDesktop && isMenuOpen && (
                    <Box {...styles.hamburgerIcon} onClick={onMenuClick}></Box>
                )}
                {children}
            </Box>
        </Box>
    )
}

Header.propTypes = {
    children: PropTypes.node,
    onMenuClick: PropTypes.func,
    onLogoClick: PropTypes.func,
    onMyAccountClick: PropTypes.func,
    onWishlistClick: PropTypes.func,
    onMyCartClick: PropTypes.func,
    searchInputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({current: PropTypes.elementType})
    ]),
    isMenuOpen: PropTypes.bool,
    isCountryModalOpen: PropTypes.bool,
    openCountryModal: PropTypes.func,
    closeCountryModal: PropTypes.func,
    hoverDelay: PropTypes.number
}

export default Header
